// Global error boundary. Without this, any uncaught render error in any
// child component dumps the entire app to a blank screen + "digest" log
// and the user gets bounced back to login. With this in place, the user
// sees a real error UI with the message + stack, and can either retry
// the offending screen or jump back to the dashboard without losing
// their session.
import React from 'react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Log to console so devtools shows the real stack (with sourcemaps).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    this.setState({ info });
  }
  reset = () => this.setState({ error: null, info: null });

  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    const message = err?.message || String(err);
    const stack = err?.stack || '';
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="w-full max-w-[640px] bg-white border border-red-200 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-[18px]">!</div>
              <div>
                <div className="text-[16px] font-bold text-red-900">Something broke on this page</div>
                <div className="text-[12.5px] text-red-800 mt-0.5">Your session is fine — you don't need to sign back in.</div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500 mb-1">Error</div>
              <div className="text-[13.5px] font-mono text-neutral-900 bg-canvas border border-neutral-200 rounded-md px-3 py-2 break-all">
                {message}
              </div>
            </div>
            {stack && (
              <details className="text-[12px]">
                <summary className="cursor-pointer text-neutral-600 font-semibold">Stack trace</summary>
                <pre className="mt-2 text-[11.5px] font-mono text-neutral-700 bg-canvas border border-neutral-200 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-[280px] overflow-y-auto">
                  {stack}
                </pre>
              </details>
            )}

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <button onClick={this.reset}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-neutral-900 hover:bg-black text-white text-[13.5px] font-semibold">
                Try again
              </button>
              <button onClick={() => { this.reset(); window.history.replaceState({}, '', '/dashboard'); window.location.reload(); }}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-white border border-neutral-200 hover:border-neutral-400 text-[13.5px] font-medium text-neutral-800">
                Go to dashboard
              </button>
              <button onClick={() => navigator.clipboard?.writeText(`${message}\n\n${stack}`)}
                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-white border border-neutral-200 hover:border-neutral-400 text-[13.5px] font-medium text-neutral-800">
                Copy details
              </button>
            </div>

            <div className="text-[11.5px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3">
              If this keeps happening, copy the error and send it to your admin.
              The page that crashed has been logged to the browser console with the full stack trace.
            </div>
          </div>
        </div>
      </div>
    );
  }
}
